import { useMemo } from 'react';
import { Box, List, Divider, IconButton, Tooltip } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { SidebarNavGroup } from './SidebarNavGroup';
import { useNavigation } from '../../../contexts/NavigationContext';
import { usePortal } from '../../../contexts/PortalContext';
import { getNavigationForPortal } from './navigationConfig';
import { LAYOUT_DIMENSIONS } from '../../../types/navigation';
import { transitions, zIndex as zIndexTokens } from '../../../theme/tokens';
import './Sidebar.css';

export function Sidebar() {
  const { isCollapsed, toggleCollapsed } = useNavigation();
  const { activePortal } = usePortal();

  // Get navigation config based on active portal
  const navigationConfig = useMemo(
    () => getNavigationForPortal(activePortal),
    [activePortal]
  );

  const sidebarWidth = isCollapsed
    ? LAYOUT_DIMENSIONS.SIDEBAR_WIDTH_COLLAPSED
    : LAYOUT_DIMENSIONS.SIDEBAR_WIDTH_EXPANDED;

  return (
    <Box
      component="nav"
      role="navigation"
      aria-label="Main navigation"
      className="sidebar"
      data-collapsed={isCollapsed}
      sx={{
        position: 'fixed',
        top: LAYOUT_DIMENSIONS.TOPBAR_HEIGHT,
        left: 0,
        bottom: 0,
        width: sidebarWidth,
        backgroundColor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: `width ${transitions.duration.normal}ms ${transitions.easing.easeInOut}`,
        zIndex: zIndexTokens.drawer,
        overflow: 'hidden',
      }}
    >
      <Box
        className="sidebar__nav-container"
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
        }}
      >
        <List component="ul" disablePadding aria-label="Navigation systems">
          {navigationConfig.systems.map((group) => (
            <SidebarNavGroup key={group.id} group={group} />
          ))}
        </List>
      </Box>

      <Divider />

      <Box
        className="sidebar__footer"
        sx={{
          p: 1,
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-end',
        }}
      >
        <Tooltip
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          placement="right"
          arrow
        >
          <IconButton
            onClick={toggleCollapsed}
            size="small"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
            sx={{
              transition: `transform ${transitions.duration.fast}ms ${transitions.easing.easeInOut}`,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
